/**
 * <s-scroll-header>: sticky header wrapper. Adds [data-scrolled] past 8px (frosted
 * compact bar). It stays visible while scrolling on every page, so it never sets
 * [data-hidden].
 *
 * Tone (merchant 2026-10-29): the scrolled bar is dark glass with light text
 * ([data-on-dark]) for the WHOLE page on dark pages, never switching to white
 * further down. A page is dark when the layout marks it [data-overlay] (layout header_overlay: every Night-tone page,
 * homepage, cart, blog, search, collections list, Premium product and collection) or when
 * the page's own background right under the header is dark (checked once on load,
 * so new dark templates follow automatically). Light pages keep the white bar.
 */
class SScrollHeader extends HTMLElement {
  #ticking = false;
  #dark = false;
  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => this.#update());
  };

  connectedCallback() {
    this.removeAttribute('data-hidden');
    this.#dark = this.hasAttribute('data-overlay') || this.#pageIsDark();
    this.toggleAttribute('data-on-dark', this.#dark);
    this.#update();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    if (!this.#dark) {
      // Content below the header may not be laid out yet; check once more when the page has loaded.
      window.addEventListener('load', () => {
        this.#dark = this.#pageIsDark();
        this.toggleAttribute('data-on-dark', this.#dark);
      }, { once: true });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
  }

  #update() {
    this.#ticking = false;
    this.toggleAttribute('data-scrolled', Math.max(window.scrollY, 0) > 8);
  }

  /** Luminance of the first painted background under the header (page content, not the header). */
  #pageIsDark() {
    const main = document.getElementById('main-content');
    if (!main) return false;
    const y = Math.min(this.offsetHeight + 32, window.innerHeight - 1);
    let element = document.elementFromPoint(window.innerWidth / 2, y);
    if (!element || this.contains(element)) element = main.firstElementChild || main;
    while (element && element !== document.documentElement) {
      const color = getComputedStyle(element).backgroundColor;
      const lightness = this.#lightness(color);
      if (lightness !== null) return lightness < 0.4;
      element = element.parentElement;
    }
    return false;
  }

  /** 0–1 lightness of an opaque-enough computed colour, or null when it is (mostly) transparent. */
  #lightness(color) {
    const parts = (color.match(/-?[\d.]+%?/g) || []).map((value) => (value.endsWith('%') ? parseFloat(value) / 100 : parseFloat(value)));
    if (parts.length < 3) return null;
    const alpha = parts.length > 3 ? parts[3] : 1;
    if (alpha <= 0.5) return null;
    if (/^okl(ab|ch)\(/.test(color)) return parts[0];
    const scale = color.startsWith('color(') ? 1 : 255;
    const [r, g, b] = parts.slice(0, 3).map((value) => value / scale);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
}

if (!customElements.get('s-scroll-header')) customElements.define('s-scroll-header', SScrollHeader);
